// hooks/market/useCreateShop.ts
//
// Crée une boutique (userpro uniquement) avec un formulaire minimal :
// nom + slug (généré automatiquement depuis le nom si non fourni,
// éditable). timezone est détectée silencieusement via
// Intl.DateTimeFormat — pas de champ formulaire pour ça.
//
// Appelle le vrai backend :
//   POST {API_BASE_URL}/market   action: 'create-shop', { name, slug?, timezone }
// (voir backend/handleMarket.ts → createShop)

import { useState, useCallback } from 'react';
import { API_BASE_URL } from '@/lib/api';
import { getValidToken } from '@/lib/auth';

export type CreatedShop = {
  id: string;
  name: string;
  slug: string;
  status: string;
  created_at: string;
};

export function useCreateShop() {
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState<string | null>(null);

  const createShop = useCallback(async (name: string, slug?: string): Promise<CreatedShop | null> => {
    setLoading(true);
    setError(null);

    try {
      const token = await getValidToken();
      if (!token) {
        setError('session_expired');
        return null;
      }

      // Détection silencieuse — pas demandée à l'utilisateur.
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

      const res = await fetch(`${API_BASE_URL}/market`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action:       'create-shop',
          access_token: token,
          name,
          slug,
          timezone,
        }),
      });

      const data = await res.json();

      if (!res.ok || !data.success) {
        setError(data.error || 'Impossible de créer la boutique');
        return null;
      }

      return data.shop as CreatedShop;
    } catch (err) {
      console.error('[useCreateShop]', err);
      setError('network_error');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  return { createShop, loading, error };
}
